import { CACHE_CONSTANTS } from '../constants/cacheConstants';

export interface CacheManagerOptions {
  onSessionExpired?: () => void;
  onCacheCleared?: () => void;
}

export class CacheManager {
  private static instance: CacheManager;
  private options: CacheManagerOptions = {};

  public constructor() {}

  public static getInstance(options?: CacheManagerOptions): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    if (options) {
      CacheManager.instance.options = options;
    }
    return CacheManager.instance;
  }

  /**
   * Check if the current session has expired (24 hours)
   */
  public isSessionExpired(): boolean {
    const cacheTimestamp = localStorage.getItem(CACHE_CONSTANTS.CACHE_KEYS.CACHE_TIMESTAMP);
    
    if (!cacheTimestamp) {
      return true; // No timestamp means expired
    }

    const timestamp = parseInt(cacheTimestamp, 10);
    const now = Date.now();
    const timeDiff = now - timestamp;

    return timeDiff >= CACHE_CONSTANTS.SESSION_TIMEOUT;
  }

  /**
   * Initialize cache manager and check for session expiration
   */
  public initialize(options?: CacheManagerOptions): boolean {
    if (options) {
      this.options = options;
    }

    const sessionExpired = this.isSessionExpired();
    
    if (sessionExpired) {
      this.clearExpiredCache();
      if (this.options.onSessionExpired) {
        this.options.onSessionExpired();
      }
    }

    return sessionExpired;
  }

  /**
   * Clear expired cache data while preserving specific keys
   */
  public clearExpiredCache(): void {
    // Get all keys to preserve
    const preservedData: { [key: string]: string | null } = {};
    
    CACHE_CONSTANTS.PRESERVED_KEYS.forEach(key => {
      preservedData[key] = localStorage.getItem(key);
    });

    // Clear all localStorage
    localStorage.clear();

    // Restore preserved data
    Object.entries(preservedData).forEach(([key, value]) => {
      if (value !== null) {
        localStorage.setItem(key, value);
      }
    });

    // Set new cache timestamp
    this.updateCacheTimestamp();

    if (this.options.onCacheCleared) {
      this.options.onCacheCleared();
    }
  }

  /**
   * Clear only application state (not intro modal, email, zipcode)
   */
  public clearApplicationState(): void {
    const preservedData: { [key: string]: string | null } = {};
    
    CACHE_CONSTANTS.PRESERVED_KEYS.forEach(key => {
      preservedData[key] = localStorage.getItem(key);
    });

    // Clear all localStorage
    localStorage.clear();

    // Restore preserved data
    Object.entries(preservedData).forEach(([key, value]) => {
      if (value !== null) {
        localStorage.setItem(key, value);
      }
    });

    // Set new cache timestamp
    this.updateCacheTimestamp();
  }

  /**
   * Force clear all cache (including preserved keys)
   */
  public forceClearAllCache(): void {
    localStorage.clear();
    this.updateCacheTimestamp();
  }

  /**
   * Update cache timestamp
   */
  public updateCacheTimestamp(): void {
    localStorage.setItem(CACHE_CONSTANTS.CACHE_KEYS.CACHE_TIMESTAMP, Date.now().toString());
  }

  /**
   * Get cache age in milliseconds
   */
  public getCacheAge(): number {
    const cacheTimestamp = localStorage.getItem(CACHE_CONSTANTS.CACHE_KEYS.CACHE_TIMESTAMP);
    if (!cacheTimestamp) return 0;
    
    const timestamp = parseInt(cacheTimestamp, 10);
    return Date.now() - timestamp;
  }

  /**
   * Get remaining session time in milliseconds
   */
  public getRemainingSessionTime(): number {
    const age = this.getCacheAge();
    return Math.max(0, CACHE_CONSTANTS.SESSION_TIMEOUT - age);
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();
